/**
 * `reports` endpoint wrappers — four read-only aggregation endpoints, no CRUD.
 *
 * Response shapes are derived directly from
 * refactor/backend/src/modules/reports/routes.ts (there is no serializers.ts
 * for this module) — see src/types/report.ts for the full derivation notes.
 */
import { apiClient } from '../client';
import {
  REPORTS_DASHBOARD,
  REPORTS_FINANCIAL,
  REPORTS_MAINTENANCE,
  REPORTS_RENT_ROLL,
  REPORTS_TAX_SUMMARY,
} from './paths';
import type { Dashboard, FinancialReport, MaintenanceReport, RentRoll, TaxSummary } from '../../types/report';

/** `GET reports/dashboard/` — shape branches by role; see `isTenantDashboard`. */
export async function getDashboard(): Promise<Dashboard> {
  const response = await apiClient.get<Dashboard>(REPORTS_DASHBOARD);
  return response.data;
}

/** `GET reports/rent-roll/` — manager roles only. */
export async function getRentRoll(): Promise<RentRoll> {
  const response = await apiClient.get<RentRoll>(REPORTS_RENT_ROLL);
  return response.data;
}

/**
 * `GET reports/rent-roll/?format=csv` — the same rows as `getRentRoll`, as ONE
 * combined CSV sheet (upcoming and overdue, distinguished by a `section`
 * column). NON-PARITY: Django never implemented this export.
 *
 * Returns the raw `Blob` rather than a parsed body — this is the one endpoint
 * in this module that is not JSON.
 */
export async function getRentRollCsv(): Promise<Blob> {
  const response = await apiClient.get<Blob>(REPORTS_RENT_ROLL, {
    params: { format: 'csv' },
    responseType: 'blob',
  });
  return response.data;
}

/** `GET reports/maintenance/` — manager roles only. */
export async function getMaintenanceReport(): Promise<MaintenanceReport> {
  const response = await apiClient.get<MaintenanceReport>(REPORTS_MAINTENANCE);
  return response.data;
}

/** `GET reports/financial/` — manager roles only. */
export async function getFinancialReport(): Promise<FinancialReport> {
  const response = await apiClient.get<FinancialReport>(REPORTS_FINANCIAL);
  return response.data;
}

/** `GET reports/tax-summary/?year=YYYY` — manager roles only. Defaults to the current year when `year` is omitted. */
export async function getTaxSummary(year?: number): Promise<TaxSummary> {
  const response = await apiClient.get<TaxSummary>(REPORTS_TAX_SUMMARY, {
    params: year === undefined ? undefined : { year },
  });
  return response.data;
}
